// Shared context for a running simulation. The Shell fetches the simulation
// config (company, people, theme, labels, columns) once from the API and makes
// it available to every child (board, ticket, wiki, dashboard, ui atoms) so no
// component imports hardcoded config anymore. Also exposes keyed lookups for
// people and labels.
import { createContext, useContext, useMemo } from "react";

export const SimulationContext = createContext(null);

export function useSimulation() {
  const ctx = useContext(SimulationContext);
  if (!ctx) {
    throw new Error("useSimulation must be used within a SimulationProvider");
  }
  return ctx;
}

// Build the context value from a fetched simulation config document.
export function useSimulationValue(slug, simulation) {
  return useMemo(() => {
    const people = (simulation && simulation.people) || [];
    const labels = (simulation && simulation.labels) || [];
    const peopleByKey = {};
    people.forEach((p) => {
      peopleByKey[p.key] = p;
    });
    const labelsByKey = {};
    labels.forEach((l) => {
      labelsByKey[l.key] = l;
    });
    // The learner ("you") for convenience.
    const you = people.find((p) => p.isYou) || peopleByKey.you || null;
    return {
      slug,
      simulation,
      company: (simulation && simulation.company) || {},
      columns: (simulation && simulation.columns) || [],
      theme: (simulation && simulation.theme) || {},
      people,
      peopleByKey,
      labelsByKey,
      you,
    };
  }, [slug, simulation]);
}

// Resolve a person prop that may be a key string or an object, against the
// simulation's cast.
export function resolvePerson(person, peopleByKey) {
  if (!person) return null;
  if (typeof person === "string") return peopleByKey[person] || null;
  return person;
}
